/**
 * BackgroundModel.js
 * ──────────────────
 * Canonical shape for background settings used across all CMS blocks.
 * Provides factory, merge, and helper methods so every consumer works
 * with the same structure — mirrors the pattern of TypographyModel.js.
 *
 * Usage:
 *   import BackgroundModel from '../../models/BackgroundModel';
 *
 *   // Create with defaults
 *   var bg = BackgroundModel.create();
 *
 *   // Merge saved data (handles missing keys from older versions)
 *   var bg = BackgroundModel.create({ backgroundColor: '#1E2031' });
 *
 *   // Build from prefixed block props  (e.g. sectionBackgroundColor → backgroundColor)
 *   var bg = BackgroundModel.fromProps(blockProps, 'section');
 *
 *   // Write back to prefixed block props
 *   var changes = BackgroundModel.toProps(bg, 'section');
 *   // → { sectionBackgroundColor: '#1E2031', sectionBackgroundImage: '', ... }
 *
 *   // Get the CSS style object
 *   var css = BackgroundModel.toCssObject(bg);
 */

import { buildBackgroundStyle, buildBackgroundOverlayStyle, capitalize } from '../blocks/shared/blockUtils.js';

/**
 * The ordered list of keys that make up a background model.
 * This is the single source of truth — every helper iterates this list.
 */
var BACKGROUND_KEYS = [
	'backgroundColor',
	'backgroundImage',
	'backgroundSize',
	'backgroundPosition',
	'backgroundRepeat',
	'backgroundOverlay',
	'backgroundOverlayOpacity'
];

/**
 * Default values for background fields when not provided.
 */
var BACKGROUND_DEFAULTS = {
	backgroundColor: '',
	backgroundImage: '',
	backgroundSize: 'cover',
	backgroundPosition: 'center center',
	backgroundRepeat: 'no-repeat',
	backgroundOverlay: '',
	backgroundOverlayOpacity: '0.5'
};

/**
 * Create a background model with defaults, merging in any overrides.
 * Missing or undefined keys always get safe defaults.
 *
 * @param {Object} [overrides]  Partial background values to merge in
 * @returns {Object}            A complete background model
 */
function create(overrides) {
	var model = {};
	for (var i = 0; i < BACKGROUND_KEYS.length; i++) {
		var key = BACKGROUND_KEYS[i];
		model[key] = (overrides && overrides[key] !== undefined)
			? overrides[key]
			: BACKGROUND_DEFAULTS[key];
	}
	return model;
}

/**
 * Build the flat prop key for a given background key + prefix.
 *
 * @param {String} key        Background key (e.g. 'backgroundColor')
 * @param {String} prefix     Prefix (e.g. 'section')
 * @returns {String}
 */
function buildPropKey(key, prefix) {
	if (!prefix) return key;
	return prefix + capitalize(key);
}

/**
 * Build a background model by extracting prefixed keys from a flat props
 * object. Handles the common CMS pattern where block props use prefixes
 * like "sectionBackgroundColor", "cardBackgroundImage", etc.
 *
 * @param {Object} props          Flat props object (e.g. block.props)
 * @param {String} [prefix='']    Key prefix (e.g. 'section', 'card')
 * @param {Object} [defaults={}]  Default values if prop is missing
 * @returns {Object}              A complete background model
 */
function fromProps(props, prefix, defaults) {
	if (!props) props = {};
	if (!defaults) defaults = {};
	var model = {};
	for (var i = 0; i < BACKGROUND_KEYS.length; i++) {
		var key = BACKGROUND_KEYS[i];
		var propKey = buildPropKey(key, prefix || '');
		model[key] = props[propKey] !== undefined
			? props[propKey]
			: (defaults[key] !== undefined ? defaults[key] : BACKGROUND_DEFAULTS[key]);
	}
	return model;
}

/**
 * Convert a background model back into a flat prefixed-key object
 * suitable for committing to block props.
 *
 * @param {Object} model          Background model
 * @param {String} [prefix='']    Key prefix
 * @returns {Object}              Flat object with prefixed keys
 */
function toProps(model, prefix) {
	if (!model) model = {};
	var result = {};
	for (var i = 0; i < BACKGROUND_KEYS.length; i++) {
		var key = BACKGROUND_KEYS[i];
		var propKey = buildPropKey(key, prefix || '');
		result[propKey] = model[key] !== undefined ? model[key] : BACKGROUND_DEFAULTS[key];
	}
	return result;
}

/**
 * Return a new model with a single key updated (immutable pattern).
 *
 * @param {Object} model   Current background model
 * @param {String} key     The background key to update
 * @param {String} value   The new value
 * @returns {Object}       New model with the updated key
 */
function set(model, key, value) {
	var updated = create(model);
	updated[key] = value;
	return updated;
}

/**
 * Build a CSS style object for the background.
 * Delegates to buildBackgroundStyle() from blockUtils.js.
 *
 * @param {Object} model   A background model
 * @returns {Object}       CSS style object safe to bind with :style
 */
function toCssObject(model) {
	return buildBackgroundStyle(model || {});
}

/**
 * Build a CSS style object for the background overlay layer.
 * Delegates to buildBackgroundOverlayStyle() from blockUtils.js.
 *
 * @param {Object} model   A background model
 * @returns {Object}       CSS style object for the overlay div
 */
function toOverlayCssObject(model) {
	return buildBackgroundOverlayStyle(model || {});
}

/**
 * Check whether two background models are equal.
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {Boolean}
 */
function isEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	for (var i = 0; i < BACKGROUND_KEYS.length; i++) {
		var key = BACKGROUND_KEYS[i];
		if ((a[key] || '') !== (b[key] || '')) return false;
	}
	return true;
}

/**
 * Get the list of background property keys.
 * @returns {String[]}
 */
function keys() {
	return BACKGROUND_KEYS.slice();
}

var BackgroundModel = {
	KEYS: BACKGROUND_KEYS,
	DEFAULTS: BACKGROUND_DEFAULTS,
	create: create,
	fromProps: fromProps,
	toProps: toProps,
	buildPropKey: buildPropKey,
	set: set,
	toCssObject: toCssObject,
	toOverlayCssObject: toOverlayCssObject,
	isEqual: isEqual,
	keys: keys
};

export default BackgroundModel;
